// src/components/AccessDeniedModal.jsx
import React, { useEffect, useRef } from "react";
import { LockKeyhole } from "lucide-react";

export default function AccessDeniedModal({
  open,
  onClose,
  title,
  description,
  actions = [],
}) {
  const backdropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/75 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="access-denied-title"
    >
      <div className="w-[92%] max-w-md rounded-2xl border border-slate-800/80 bg-slate-950/95 p-6 shadow-[0_24px_70px_rgba(15,23,42,0.95)] animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-xl bg-sky-500/15 p-2 text-sky-400 ring-1 ring-sky-500/30">
            <LockKeyhole className="h-4 w-4" />
          </div>
          <div>
            <h2
              id="access-denied-title"
              className="text-lg font-bold tracking-tight text-slate-50"
            >
              {title || "This page requires NFL LITE or PRO"}
            </h2>
            <p className="mt-1 text-sm text-slate-300">
              {description ||
                "Your current plan is free (inactive). Upgrade to unlock NFL tools, projections, and betting data for this page."}
            </p>
          </div>
        </div>

        {/* Access options */}
        <div className="mt-5 rounded-2xl border border-slate-800/80 bg-slate-950/90 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            How to get access
          </div>
          <div className="mt-3 space-y-2">
            {actions.map((action, idx) => {
              const content = (
                <span className="flex w-full items-center justify-center gap-2">
                  <span>{action.label}</span>
                  {action.tag && (
                    <span className="rounded-full bg-slate-950/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-100/90">
                      {action.tag}
                    </span>
                  )}
                </span>
              );

              if (action.onClick && !action.href) {
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={action.onClick}
                    className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-sky-500 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-sky-500/25 transition hover:from-sky-400 hover:to-indigo-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                  >
                    {content}
                  </button>
                );
              }

              return (
                <a
                  key={idx}
                  href={action.href}
                  className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-sky-500 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-lg shadow-sky-500/25 transition hover:from-sky-400 hover:to-indigo-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
                >
                  {content}
                </a>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] leading-snug text-slate-400">
            Already subscribed with a different email?{" "}
            <span className="font-semibold text-sky-400">
              Log in with that account to unlock this page.
            </span>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => onClose?.()}
            className="inline-flex items-center rounded-full border border-slate-600/80 bg-slate-950/90 px-4 py-2 text-sm font-semibold text-slate-100 shadow-sm hover:border-sky-400 hover:text-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
